import {
  Alert,
  Box,
  ButtonBase,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CaseContextProps, useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import CenteredMessageBox from "../general/CenteredMessageBox";
import {
  signedBar,
  signedTone,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { TonalBadge } from "../general/TonalBadge";
import { byName, clientsFirst, sort } from "./AnalyticsUtility";
import { percentage } from "../../shared/percentage";
import { PartyValueExtended } from "./NetResultChart";
import { ChanceArrow, useVsLitigation, VsLitigationLine } from "./VsLitigation";
import { useLitigation } from "./ShownResults";
import {
  chanceOfPositive,
  netResultComparison,
  NetResultsOf,
} from "../../shared/adjustedView";
import { GuideHeader } from "../guide/GuideHeader";

/** How strongly the row of the selected party is washed with the primary colour. */
const SELECTED_ROW_ALPHA = 0.04;

/**
 * An end of the scale is named unless zero stands this close to it, in % of
 * the track: the two labels would run into each other.
 */
const MIN_LABEL_DISTANCE_PCT = 14;

/** The width of the name of a party beside its bar, in theme spacing. */
const NAME_COLUMN = 25;

export const NetResultAnalytics = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.netResult",
  });
  const caseContext = useCase();
  const parties = caseContext.getPartyDescriptors();

  const netResults: PartyValueExtended[] = useMemo(() => {
    const data: PartyValueExtended[] = [];
    sort(parties, clientsFirst, byName).forEach((partyId) => {
      const netResult = caseContext.getPartyStats(partyId)?.stats.netResult;
      if (!netResult) return;
      data.push({
        client: parties[partyId].isClient ?? false,
        netResult,
        partyId,
        partyName: parties[partyId].name,
      });
    });
    return data;
  }, [parties, caseContext]);

  // in the settlement-adjusted view, the case as litigated to set it against
  const litigation = useLitigation();
  const adjusted = caseContext.getCase().settlementAdjusted;
  const settling: Settling = {
    adjustedView: caseContext !== litigation,
    parties: Object.fromEntries(
      netResults.map(({ partyId }) => {
        const ofAdjusted =
          adjusted?.status === "done"
            ? adjusted.parties.find((party) => party.partyId === partyId)
            : undefined;
        return [
          partyId,
          {
            shown: netResultsOf(caseContext, partyId),
            litigated: netResultsOf(litigation, partyId),
            adjustedRange: ofAdjusted?.stats.netResult,
          },
        ];
      }),
    ),
  };

  if (netResults.length === 0)
    return <CenteredMessageBox message={t("noData")} />;

  return <NetResults netResults={netResults} settling={settling} />;
};

/**
 * What a page sets the net results it shows against: in the settlement-adjusted
 * view the results as litigated, and in both where the settlements land.
 */
export type Settling = {
  adjustedView: boolean;
  parties: {
    [partyId: string]: {
      shown?: NetResultsOf;
      litigated?: NetResultsOf;
      // the settlement-adjusted range, kept on the scale in both views
      adjustedRange?: PartyValueExtended["netResult"];
    };
  };
};

/**
 * The net results of the parties on one scale, with the figures of the party
 * that is selected over them.
 */
export const NetResults = ({
  netResults,
  settling,
}: {
  // of at least one party
  netResults: PartyValueExtended[];
  settling?: Settling;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const tones = toneColors(theme);
  const [selectedPartyId, setSelectedPartyId] = useState(netResults[0].partyId);
  // a party that is gone with a change of the case gives way to the first
  const selected =
    netResults.find(({ partyId }) => partyId === selectedPartyId) ??
    netResults[0];
  const adjustedView = settling?.adjustedView ?? false;
  const settlingOf = (partyId: string) => settling?.parties[partyId];

  // a party with a single net result has it as its worst and its best
  const rangeOf = ({ average, worst, best }: PartyValueExtended["netResult"]) =>
    ({ worst: worst ?? average, best: best ?? average }) as const;
  const ranges = netResults.map(({ netResult }) => rangeOf(netResult));
  // One scale for both views, so that switching moves the marks and not the
  // scale: it spans the litigated and the settlement-adjusted results. It
  // runs through zero, so a bar shows which side of it it is on.
  const values = netResults.flatMap(({ partyId, netResult }) => {
    const litigated = settlingOf(partyId)?.litigated?.stats.netResult;
    const ofAdjusted = settlingOf(partyId)?.adjustedRange;
    return [
      ...Object.values(rangeOf(netResult)),
      ...(litigated ? Object.values(rangeOf(litigated)) : []),
      ...(ofAdjusted ? Object.values(rangeOf(ofAdjusted)) : []),
    ];
  });
  const lowest = Math.min(0, ...values);
  const highest = Math.max(0, ...values);
  const span = highest - lowest || 1;
  const at = (value: number) => ((value - lowest) / span) * 100;
  const zeroPct = at(0);

  const { average } = selected.netResult;
  const { worst, best } = rangeOf(selected.netResult);
  const shownOf = settlingOf(selected.partyId)?.shown;
  const litigatedOf = settlingOf(selected.partyId)?.litigated;
  const compared =
    adjustedView && shownOf && litigatedOf
      ? netResultComparison(litigatedOf, shownOf)
      : undefined;
  const vsLitigation = useVsLitigation();
  const lineOf = ({ text, tone }: VsLitigationLine) => (
    <Caption tone={tone && tones[tone]}>{text}</Caption>
  );
  const changeCaption = (change: number) => lineOf(vsLitigation.change(change));
  const endCaption = (end: Parameters<typeof vsLitigation.end>[0]) =>
    lineOf(vsLitigation.end(end));
  const positiveChance = shownOf && chanceOfPositive(shownOf.outcomes);

  const figureLabel = {
    typography: "caption",
    fontWeight: 500,
    color: "text.secondary",
  } as const;
  const figure = {
    fontWeight: 700,
    letterSpacing: "-.015em",
    lineHeight: 1.25,
    ...TABULAR_NUMERALS,
  } as const;
  const scaleLabel = {
    position: "absolute",
    typography: "caption",
    color: "text.secondary",
    ...TABULAR_NUMERALS,
  } as const;

  return (
    <Stack spacing={2.25} sx={{ userSelect: "none" }}>
      {/* the header, and at its end which results the page shows */}
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("analytics.netResult.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
          >
            {t("analytics.netResult.description")}
          </Typography>
        </Box>
      </GuideHeader>

      {/* the figures of the selected party */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2.5 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.125} sx={{ alignItems: "center" }}>
            <Typography component="h2" sx={{ fontWeight: 700 }}>
              {selected.partyName}
            </Typography>
            {selected.client && (
              <TonalBadge tone={theme.palette.primary}>
                {t("parties.clientBadge")}
              </TonalBadge>
            )}
          </Stack>
          {average < 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {t("analytics.netResult.warning.negative", {
                name: selected.partyName,
              })}
            </Alert>
          )}
        </Box>
        <Box
          sx={{
            // as wide as each figure needs, wrapping where the row is full:
            // a long amount takes the room it needs instead of running on
            display: "flex",
            flexWrap: "wrap",
            columnGap: 3.5,
            rowGap: 2.25,
            mt: 2,
            "& > div": { flex: "1 1 220px", minWidth: "max-content" },
          }}
        >
          <div>
            <Typography sx={figureLabel}>
              {t("analytics.netResult.average")}
            </Typography>
            <Typography
              variant="h4"
              sx={{ ...figure, color: tones[signedTone(average)] }}
            >
              {currency.format(average)}
            </Typography>
            {compared ? changeCaption(compared.average) : <Caption />}
          </div>
          <div>
            <Typography sx={figureLabel}>
              {t("analytics.netResult.worst")}
            </Typography>
            <Typography variant="h5" sx={figure}>
              {currency.format(worst)}
            </Typography>
            {compared ? (
              endCaption(compared.worst)
            ) : (
              // a single net result is no distance from its average
              <Caption tone={tones.adverse}>
                {worst < average &&
                  t("analytics.netResult.belowAverage", {
                    amount: currency.format(average - worst),
                  })}
              </Caption>
            )}
          </div>
          <div>
            <Typography sx={figureLabel}>
              {t("analytics.netResult.best")}
            </Typography>
            <Typography variant="h5" sx={figure}>
              {currency.format(best)}
            </Typography>
            {compared ? (
              endCaption(compared.best)
            ) : (
              <Caption tone={tones.favourable}>
                {best > average &&
                  t("analytics.netResult.aboveAverage", {
                    amount: currency.format(best - average),
                  })}
              </Caption>
            )}
          </div>
          {positiveChance !== undefined && (
            <div>
              <Typography sx={figureLabel}>
                {t("analytics.netResult.positiveChance")}
              </Typography>
              <Typography
                variant="h5"
                sx={{ ...figure, display: "flex", alignItems: "baseline" }}
              >
                {percentage.format(positiveChance)}
                {compared && (
                  <ChanceArrow
                    chance={compared.positive.chance}
                    without={compared.positive.without}
                    of={t("analytics.netResult.positiveOf")}
                  />
                )}
              </Typography>
              <Caption>
                {compared &&
                  t("shownResults.inLitigation", {
                    chance: percentage.format(compared.positive.without),
                  })}
              </Caption>
            </div>
          )}
        </Box>
      </Paper>

      {/* all parties on one scale */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2.25 }}
      >
        <SectionLabel component="h2">
          {t("analytics.netResult.netResultTitle")}
        </SectionLabel>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, mb: 1.75, color: "text.secondary" }}
        >
          {t("analytics.netResult.ranges")}
        </Typography>

        <Stack spacing={1}>
          {netResults.map(({ partyId, partyName, netResult }, index) => {
            const isSelected = partyId === selected.partyId;
            const range = ranges[index];
            const tone = tones[signedTone(netResult.average)];
            return (
              <ButtonBase
                key={partyId}
                onClick={() => setSelectedPartyId(partyId)}
                aria-pressed={isSelected}
                sx={{
                  display: "grid",
                  gridTemplateColumns: `${theme.spacing(NAME_COLUMN)} minmax(0, 1fr)`,
                  gap: 2,
                  alignItems: "center",
                  textAlign: "left",
                  px: 1.75,
                  py: 1.5,
                  borderRadius: 2.25,
                  border: 1,
                  borderColor: isSelected ? "primary.main" : "divider",
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, SELECTED_ROW_ALPHA)
                    : "transparent",
                }}
              >
                <span>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ display: "block", fontWeight: 600 }}
                  >
                    {partyName}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      display: "block",
                      fontWeight: 600,
                      color: tone,
                      ...TABULAR_NUMERALS,
                    }}
                  >
                    {t("analytics.netResult.onAverage", {
                      amount: currency.format(netResult.average),
                    })}
                  </Typography>
                </span>
                <Box
                  component="span"
                  sx={{ display: "block", position: "relative", height: 30 }}
                >
                  {/* the track, the party's range on it, its average and zero */}
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: 11,
                      height: 8,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      top: 10,
                      height: 10,
                      left: `${at(range.worst)}%`,
                      width: `${at(range.best) - at(range.worst)}%`,
                      minWidth: 2,
                      borderRadius: 1,
                      ...signedBar(theme, netResult.average, !isSelected),
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      top: 4,
                      height: 22,
                      width: 3,
                      ml: "-1.5px",
                      left: `${at(netResult.average)}%`,
                      borderRadius: 0.5,
                      bgcolor: tone,
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${zeroPct}%`,
                      borderLeft: `1px solid ${theme.palette.text.disabled}`,
                    }}
                  />
                  {adjustedView && (
                    <SettlementMarks
                      average={netResult.average}
                      litigated={
                        settlingOf(partyId)?.litigated?.stats.netResult.average
                      }
                      at={at}
                    />
                  )}
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>

        {/* the scale, under the bars */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `${theme.spacing(NAME_COLUMN)} minmax(0, 1fr)`,
            gap: 2,
            px: 1.75,
            mt: 0.75,
          }}
        >
          <span />
          <Box sx={{ position: "relative", height: 18 }}>
            {lowest < 0 && zeroPct > MIN_LABEL_DISTANCE_PCT && (
              <Box component="span" sx={{ ...scaleLabel, left: 0 }}>
                {currency.formatCompact(lowest)}
              </Box>
            )}
            <Box
              component="span"
              sx={{
                ...scaleLabel,
                left: `${zeroPct}%`,
                // at an end of the scale the label keeps within it
                transform:
                  zeroPct === 0
                    ? "none"
                    : zeroPct === 100
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
              }}
            >
              {currency.formatCompact(0)}
            </Box>
            {highest > 0 && zeroPct < 100 - MIN_LABEL_DISTANCE_PCT && (
              <Box component="span" sx={{ ...scaleLabel, right: 0 }}>
                {currency.formatCompact(highest)}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
};

/** A party's net results as a view of the case shows them. */
const netResultsOf = (
  context: CaseContextProps,
  partyId: string,
): NetResultsOf | undefined => {
  const stats = context.getPartyStats(partyId)?.stats;
  const outcomes = context
    .getOutcomes()
    .find((ofParty) => ofParty.partyId === partyId)?.outcomes;
  return stats && outcomes ? { stats, outcomes } : undefined;
};

/** A line under a figure; one with nothing to say keeps its place. */
const Caption = ({
  tone,
  children,
}: {
  tone?: string;
  children?: React.ReactNode;
}) => (
  <Typography
    variant="caption"
    component="p"
    sx={{
      fontWeight: 600,
      color: tone ?? "text.secondary",
      minHeight: "1.66em",
      ...TABULAR_NUMERALS,
    }}
  >
    {children || "\u00a0"}
  </Typography>
);

/**
 * What settling does on a party's bar: the average in litigation as a thin
 * grey mark, and an arrow from it to the average with settlements.
 */
const SettlementMarks = ({
  average,
  litigated,
  at,
}: {
  average: number;
  litigated: number | undefined;
  at: (value: number) => number;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  if (litigated === undefined) return null;
  const from = at(litigated);
  const to = at(average);
  const grey = theme.palette.grey[500];
  return (
    <>
      {Math.abs(to - from) > 0.5 && (
        // the arrow, above the bar, from litigation to with settlements
        <Box
          component="span"
          sx={{
            position: "absolute",
            top: 1,
            height: 2,
            left: `${Math.min(from, to)}%`,
            width: `${Math.abs(to - from)}%`,
            bgcolor: grey,
            "&::after": {
              content: '""',
              position: "absolute",
              top: -3,
              [to > from ? "right" : "left"]: -2,
              borderTop: "4px solid transparent",
              borderBottom: "4px solid transparent",
              [to > from ? "borderLeft" : "borderRight"]: `6px solid ${grey}`,
            },
          }}
        />
      )}
      <Tooltip
        title={t("analytics.netResult.vsLitigation.litigatedAverage", {
          amount: currency.format(litigated),
        })}
      >
        {/* thinner and shorter than the mark of the average shown */}
        <Box
          component="span"
          sx={{
            position: "absolute",
            top: 8,
            height: 14,
            width: 2,
            ml: "-1px",
            left: `${from}%`,
            borderRadius: 0.5,
            bgcolor: grey,
          }}
        />
      </Tooltip>
    </>
  );
};
